/**
 * @fileoverview The mock-only Tenant/Target model behind Sales Pipeline's rows.
 * @description Read only while the mock API is on: each prospect's mock Tenant (by its tenant id)
 * and relevant target, and the viewer's roster record, so a row's menu keeps the actions the
 * backend does not serve yet (follow-up, activity, reassign, payment). Against the real backend it
 * issues no requests and reports itself ready at once.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useMemo } from 'react'

import { useSalesRoster } from '@/features/sales/hooks/useSalesRoster'
import { useTargets } from '@/features/sales/hooks/useTargets'
import { useTenants } from '@/features/sales/hooks/useTenants'
import { pickRelevantTarget } from '@/features/sales/lib/target-selection.utils'
import { useAuth } from '@/shared/lib/auth/useAuth'

import type { TargetWithOverdue } from '@/features/sales/mocks/targets-mock-store'
import type { ProspectArchiveStatus } from '@/features/sales/types/prospect.types'
import type { SalesUserRecord } from '@/features/sales/types/tenant.types'

const EMPTY_ROSTER: SalesUserRecord[] = []
const EMPTY_TARGETS: TargetWithOverdue[] = []

/**
 * @description The mock-only Tenant/Target model, read only while the mock API is on: each
 * prospect's mock Tenant (by its tenant id) and relevant target, and the viewer's roster record.
 * @param {ProspectArchiveStatus} archiveStatus - Which prospects the table shows.
 * @param {boolean} available - Whether the mock model is on (the mock API is enabled).
 * @returns {object} The lookups and the viewer, plus whether they've loaded.
 */
export function useLegacyPipelineModel(archiveStatus: ProspectArchiveStatus, available: boolean) {
  const { user } = useAuth()
  const enabled = { enabled: available }
  const tenantsQuery = useTenants(
    { status: 'Prospect', archived: archiveStatus === 'ARCHIVED' },
    enabled,
  )
  const targetsQuery = useTargets({}, enabled)
  const rosterQuery = useSalesRoster({}, enabled)
  const roster = rosterQuery.data ?? EMPTY_ROSTER
  const targets = targetsQuery.data ?? EMPTY_TARGETS

  const tenantsById = useMemo(
    () => new Map((tenantsQuery.data ?? []).map((tenant) => [tenant.id, tenant])),
    [tenantsQuery.data],
  )
  const targetsByTenantId = useMemo(() => {
    const byTenant = new Map<string, TargetWithOverdue[]>()
    for (const target of targets) {
      byTenant.set(target.tenantId, [...(byTenant.get(target.tenantId) ?? []), target])
    }
    const picked = new Map<string, TargetWithOverdue>()
    for (const [tenantId, tenantTargets] of byTenant) {
      const relevant = pickRelevantTarget(tenantTargets)
      if (relevant) picked.set(tenantId, relevant)
    }
    return picked
  }, [targets])

  return {
    tenantsById,
    targetsByTenantId,
    roster,
    viewerUserId: roster.find((candidate) => candidate.email === user?.email)?.id ?? '',
    isReady:
      !available || (tenantsQuery.isSuccess && targetsQuery.isSuccess && rosterQuery.isSuccess),
  }
}
